<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();
const site = useSiteData();
const { localized } = useLocaleValue();
const { phoneDisplay, phoneWhatsapp } = usePhoneHelpers();

const openingHours = computed(() => localized(site.opening_hours.labels) as {
  time: string;
  days: string;
});
</script>

<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-section">
          <h3>
            <NuxtLink :to="localePath('/')">
              {{ localized(site.website_name) }}
            </NuxtLink>
          </h3>
          <p>{{ t("footer.description") }}</p>
          <div class="footer-social">
            <a
              :href="`mailto:${localized(site.sales_email)}`"
              :aria-label="t('footer.contact.email_label')"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fas fa-envelope"></i>
            </a>
            <a
              :href="phoneWhatsapp(localized(site.phone[0]))"
              :aria-label="t('footer.contact.whatsapp_label')"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-whatsapp"></i>
            </a>
          </div>
        </div>

        <div class="footer-section">
          <h4>{{ t("footer.quick_links.title") }}</h4>
          <ul class="footer-links">
            <li>
              <NuxtLink :to="localePath('/')">
                {{ t("footer.quick_links.home") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/about/')">
                {{ t("footer.quick_links.about") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/#services')">
                {{ t("footer.quick_links.services") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/contact/')">
                {{ t("footer.quick_links.contact") }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div class="footer-section">
          <h4>{{ t("footer.services.title") }}</h4>
          <ul class="footer-links">
            <li>
              <NuxtLink :to="localePath('/#services')">
                {{ t("footer.services.logos_badges") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/#services')">
                {{ t("footer.services.labels") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/#services')">
                {{ t("footer.services.embroidery") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/#services')">
                {{ t("footer.services.custom_solutions") }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div class="footer-section">
          <h4>{{ t("footer.legal.title") }}</h4>
          <ul class="footer-links">
            <li>
              <NuxtLink :to="localePath('/privacy-policy')">
                {{ t("footer.legal.privacy_policy") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/terms-of-service')">
                {{ t("footer.legal.terms_of_service") }}
              </NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/cookie-policy')">
                {{ t("footer.legal.cookie_policy") }}
              </NuxtLink>
            </li>
            <li>
              <a href="#" id="cookie-preferences-link">
                {{ t("footer.legal.cookie_preferences") }}
              </a>
            </li>
          </ul>
        </div>

        <div class="footer-section">
          <h4>{{ t("footer.contact.title") }}</h4>
          <ul class="footer-contact">
            <li>
              📍
              <a
                :href="localized(site.maps_url)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ t("footer.contact.location") }}
              </a>
            </li>
            <li>
              📧
              <a :href="`mailto:${localized(site.sales_email)}`">
                {{ localized(site.sales_email) }}
              </a>
            </li>
            <li v-for="phone in site.phone" :key="phone">
              📞
              <a :href="`tel:${localized(phone)}`" dir="ltr">
                {{ phoneDisplay(localized(phone)) }}
              </a>
            </li>
            <li class="business-hours">
              🕒
              <span class="hours-label">{{ openingHours.time }}</span><br />
              <span class="hours-time">{{ openingHours.days }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <div class="footer-legal">
          <p>
            &copy; 2025 {{ localized(site.website_name) }}.
            {{ t("footer.bottom.rights_reserved") }}
          </p>
          <p>{{ localized(site.company_full_legal_name) }}.</p>
          <p>
            {{ t("footer.bottom.registered_prefix") }}
            {{ localized(site.cr_number) }} •
            {{ t("footer.bottom.vat_prefix") }}
            {{ localized(site.vat_number) }}
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

